// ============================================================================
// Supply Management — Purchase Order detail
// Shows vendor info + line items (item × facility × cost) + delivery details.
// Status-advance buttons for back-office roles (Sent → Acknowledged → Shipped
// → Delivered → Invoiced → Closed). Mark-delivered button per line.
// ⛔ An order with any line shipping to a stock location has NO status steps:
// a stock purchase is entered once it has arrived and is saved Closed with every
// line received (TCSD-0387). Orders with no stock lines are unchanged.
// ============================================================================

import { canEditPurchaseOrder } from '../../shared/role-gates';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import {
  ErrorState,
  PageHeader,
  StatusPill,
  UrgencyBadge,
} from '../../shared/components';
import type {
  PurchaseOrder,
  PurchaseOrderLine,
  PurchaseOrderStatus,
  SupplyUrgency,
} from '../../shared/types';
import type { POStatus } from '../../shared/design-tokens';
import { SupplySubNav } from './supply-sub-nav';
import { errorMessage } from '../../shared/errors';
import { StockReceivingSection } from './dialysis-stock/stock-receiving';

const ADVANCE_NEXT: Partial<Record<PurchaseOrderStatus, PurchaseOrderStatus>> = {
  Draft: 'Approved',
  Approved: 'Sent',
  Sent: 'Acknowledged',
  Acknowledged: 'Shipped',
  Shipped: 'Delivered',
  Delivered: 'Invoiced',
  Invoiced: 'Closed',
};


interface LineWithJoin extends PurchaseOrderLine {
  facility_name: string | null;
}

export default function PODetail() {
  const { poId = '' } = useParams();
  const { profile } = useAuth();
  const canWrite = canEditPurchaseOrder(profile);

  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<LineWithJoin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: poData, error: poErr } = await supabase
        .from('purchase_orders')
        .select('*')
        .eq('po_id', poId)
        .maybeSingle();
      if (poErr) throw poErr;
      setPo((poData ?? null) as unknown as PurchaseOrder | null);

      const { data: lineData, error: lineErr } = await supabase
        .from('purchase_order_lines')
        .select('*')
        .eq('po_id', poId)
        .order('line_id', { ascending: true });
      if (lineErr) throw lineErr;
      const lineRows = (lineData ?? []) as unknown as PurchaseOrderLine[];

      const facIds = Array.from(
        new Set(lineRows.map((l) => l.facility_id).filter(Boolean)),
      ) as string[];
      const facMap: Record<string, string | null> = {};
      if (facIds.length > 0) {
        const { data: facs } = await supabase
          .from('facilities')
          .select('facility_id, facility_name')
          .in('facility_id', facIds);
        for (const f of (facs ?? []) as Array<{
          facility_id: string;
          facility_name: string | null;
        }>) {
          facMap[f.facility_id] = f.facility_name;
        }
      }

      setLines(
        lineRows.map((l) => ({
          ...l,
          facility_name: l.facility_id ? facMap[l.facility_id] ?? null : null,
        })),
      );
    } catch (e) {
      console.error('[po-detail] load failed', e);
      setError(errorMessage(e, 'Failed to load order.'));
    } finally {
      setLoading(false);
    }
  }, [poId]);

  useEffect(() => {
    load();
  }, [load]);

  // Lines shipping to a stock location (is_surplus + surplus_storage_location_id).
  const hasStockLines = lines.some(
    (l) => !!(l as { surplus_storage_location_id?: string | null }).surplus_storage_location_id,
  );

  const advance = async () => {
    if (!po) return;
    const next = ADVANCE_NEXT[po.status as PurchaseOrderStatus];
    if (!next) return;
    if (!confirm(`Advance PO ${po.po_id} → ${next}?`)) return;
    setBusy(true);
    try {
      const { error: e } = await supabase
        .from('purchase_orders')
        .update({ status: next })
        .eq('po_id', po.po_id);
      if (e) throw e;
      await load();
    } catch (e) {
      alert(`Update failed: ${errorMessage(e, String(e))}`);
    } finally {
      setBusy(false);
    }
  };

  const markLineDelivered = async (line: LineWithJoin) => {
    if (!confirm(`Mark ${line.item_name ?? line.line_id} as Delivered?`)) return;
    setBusy(true);
    try {
      const { error: e } = await supabase
        .from('purchase_order_lines')
        .update({
          line_status: 'Delivered',
          actual_delivery: new Date().toISOString().slice(0, 10),
        })
        .eq('line_id', line.line_id);
      if (e) throw e;
      await load();
    } catch (e) {
      alert(`Update failed: ${errorMessage(e, String(e))}`);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        <SupplySubNav />
        <ErrorState body={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-4 pb-28 sm:p-6">
      <SupplySubNav />

      <Link
        to="/supply/orders"
        className="mb-3 inline-flex items-center text-sm text-tcs-text-secondary hover:text-tcs-text"
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        Back to orders
      </Link>

      {loading || !po ? (
        <div className="text-sm text-tcs-text-secondary">Loading…</div>
      ) : (
        <>
          <PageHeader
            title={po.po_id}
            meta={`${po.vendor_name ?? po.vendor_id ?? 'No vendor'} · ${po.po_date ?? ''}`}
            actions={
              <div className="flex items-center gap-2">
                <StatusPill variant="po" value={(po.status as POStatus) ?? 'Draft'} />
                {po.urgency && (
                  <UrgencyBadge tier={po.urgency as SupplyUrgency} />
                )}
                {canWrite && !hasStockLines && ADVANCE_NEXT[po.status as PurchaseOrderStatus] && (
                  <button
                    disabled={busy}
                    onClick={advance}
                    className="rounded-md bg-tcs-inverse px-3 py-1.5 text-sm font-medium text-tcs-inverse-ink hover:opacity-90 disabled:opacity-50"
                  >
                    Advance → {ADVANCE_NEXT[po.status as PurchaseOrderStatus]}
                  </button>
                )}
              </div>
            }
          />

          <section className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Box label="Vendor">{po.vendor_name ?? po.vendor_id ?? '—'}</Box>
            <Box label="Ship to">{po.ship_to_address ?? po.facility_name ?? '—'}</Box>
            <Box label="Expected delivery">{po.expected_delivery ?? '—'}</Box>
            <Box label="Actual delivery">{po.actual_delivery ?? '—'}</Box>
            <Box label="Subtotal">{fmtMoney(po.subtotal)}</Box>
            <Box label="Total">{fmtMoney(po.total)}</Box>
            <Box label="Payment method">{po.payment_method ?? '—'}</Box>
            <Box label="Invoice">{po.invoice_number ?? '—'}</Box>
          </section>

          <section className="mt-6">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
              Lines · {lines.length}
            </h2>
            <ul className="space-y-2">
              {lines.map((l) => (
                <li
                  key={l.line_id}
                  className="rounded-md border border-tcs-border bg-tcs-surface p-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-tcs-text">
                        {Number(l.qty)} {l.unit ?? ''} ×{' '}
                        {l.item_name ?? l.supply_id ?? l.line_id}
                      </div>
                      <div className="mt-1 text-xs text-tcs-text-secondary">
                        {l.facility_name ?? l.facility_id ?? 'Unallocated'}
                        {l.unit_price != null
                          ? ` · $${Number(l.unit_price).toFixed(2)} ea`
                          : ''}
                        {l.line_status ? ` · ${l.line_status}` : ''}
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="font-semibold text-tcs-text">
                        {fmtMoney(l.extended_price)}
                      </div>
                      {/* A line shipping to a stock location is received in the
                          stock section below, which records what arrived. */}
                      {canWrite && l.line_status !== 'Delivered' &&
                        l.line_status !== 'Verified' &&
                        !(l as { surplus_storage_location_id?: string | null }).surplus_storage_location_id && (
                          <button
                            disabled={busy}
                            onClick={() => markLineDelivered(l)}
                            className="mt-1 rounded-md border border-tcs-border bg-tcs-raised px-2 py-0.5 text-xs hover:bg-tcs-bg disabled:opacity-50"
                          >
                            Mark delivered
                          </button>
                        )}
                    </div>
                  </div>
                </li>
              ))}
              {lines.length === 0 && (
                <li className="text-sm text-tcs-text-secondary">
                  No line items on this PO yet.
                </li>
              )}
            </ul>
          </section>

          {/* 2026-09-23 — renders only when a line ships to a stock location. */}
          <StockReceivingSection poId={po.po_id} po={po} />
        </>
      )}
    </div>
  );
}

function Box({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-tcs-border bg-tcs-surface p-3">
      <div className="text-xs uppercase tracking-wide text-tcs-text-secondary">
        {label}
      </div>
      <div className="mt-1 text-sm text-tcs-text">{children}</div>
    </div>
  );
}

function fmtMoney(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${Number(v).toFixed(2)}`;
}
